import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { 
  GET_ALL_SUBSCRIPTION_PACKAGES, 
  CREATE_SUBSCRIPTION_PACKAGE, 
  UPDATE_SUBSCRIPTION_PACKAGE, 
  DELETE_SUBSCRIPTION_PACKAGE 
} from '../graphql/subscriptionPackageOperations';
import { 
  TrashIcon, 
  PencilIcon, 
  PlusIcon
} from '@heroicons/react/24/solid';
import { handleGraphQLError } from '../utils/errorHandler';
import { toast } from 'react-toastify';
import { useTheme } from '../contexts/ThemeContext';
import ManagementModal from '../components/ManagementModal';

const SubscriptionPackageManagement = () => {
  const { isDarkMode } = useTheme();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [packageData, setPackageData] = useState({
    name: '',
    description: '',
    duration: '',
    price: ''
  });
  const [editingPackage, setEditingPackage] = useState(null);

  // Fetch all subscription packages
  const { loading, error, data, refetch } = useQuery(GET_ALL_SUBSCRIPTION_PACKAGES, {
    variables: {
      filter: {
        duration: null,
        name: null
      }
    },
    onError: (error) => handleGraphQLError(error, 'Failed to load subscription packages')
  });

  // Mutations
  const [createSubscriptionPackage] = useMutation(CREATE_SUBSCRIPTION_PACKAGE, {
    onCompleted: () => {
      resetForm();
      refetch();
      toast.success('Subscription package created successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to create subscription package')
  });

  const [updateSubscriptionPackage] = useMutation(UPDATE_SUBSCRIPTION_PACKAGE, {
    onCompleted: () => {
      resetForm();
      refetch();
      toast.success('Subscription package updated successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to update subscription package')
  });

  const [deleteSubscriptionPackage] = useMutation(DELETE_SUBSCRIPTION_PACKAGE, {
    onCompleted: () => {
      refetch();
      toast.success('Subscription package deleted successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to delete subscription package')
  });

  const resetForm = () => {
    setPackageData({
      name: '',
      description: '',
      duration: '',
      price: ''
    });
    setEditingPackage(null);
    setIsModalOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!packageData.name.trim()) {
      toast.error('Package name is required');
      return;
    }
    if (!packageData.duration || packageData.duration <= 0) {
      toast.error('Duration must be greater than 0');
      return;
    }
    if (!packageData.price || packageData.price < 0) {
      toast.error('Price must be 0 or greater');
      return;
    }

    const input = {
      name: packageData.name.trim(),
      description: packageData.description.trim() || null,
      duration: parseInt(packageData.duration),
      price: parseFloat(packageData.price)
    };

    if (editingPackage) {
      updateSubscriptionPackage({
        variables: {
          input,
          updateSubscriptionPackageId: editingPackage.id
        }
      });
    } else {
      createSubscriptionPackage({
        variables: { input }
      });
    }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setPackageData({
      name: pkg.name,
      description: pkg.description || '',
      duration: pkg.duration.toString(),
      price: pkg.price.toString()
    });
    setIsModalOpen(true);
  };

  const handleDelete = (packageId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this subscription package?');
    if (confirmDelete) {
      deleteSubscriptionPackage({
        variables: { deleteSubscriptionPackageId: packageId }
      });
    }
  };

  // Loading and error states
  if (loading) return (
    <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
      Loading subscription packages...
    </div>
  );

  if (error) return (
    <div className={`text-center py-8 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`}>
      Error loading subscription packages
    </div>
  );

  const packages = data?.getAllSubscriptionPackages || [];

  return (
    <div className="space-y-6">
      {/* Add Package Button */}
      <div className="flex justify-end mb-4">
        <button 
          onClick={() => {
            setEditingPackage(null);
            setIsModalOpen(true);
          }}
          className={`
            flex items-center space-x-2 px-4 py-2 rounded-lg
            ${isDarkMode 
              ? 'bg-blue-600 text-white hover:bg-blue-500' 
              : 'bg-blue-500 text-white hover:bg-blue-600'
            }
            transition
          `}
        >
          <PlusIcon className="h-5 w-5" />
          <span>Add Subscription Package</span>
        </button>
      </div>

      {/* Package Management Modal */}
      <ManagementModal
        isOpen={isModalOpen}
        onClose={() => {
          resetForm();
          setIsModalOpen(false);
        }}
        onSubmit={handleSubmit}
        title={editingPackage ? 'Edit Subscription Package' : 'Create New Subscription Package'}
      >
        {/* Package Name Input */}
        <div>
          <label 
            htmlFor="name" 
            className={`block mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
          >
            Package Name *
          </label>
          <input
            type="text"
            id="name"
            value={packageData.name}
            onChange={(e) => setPackageData({...packageData, name: e.target.value})}
            className={`
              w-full px-3 py-2 rounded-lg
              ${isDarkMode 
                ? 'bg-gray-700 text-gray-200 border-gray-600 focus:border-blue-500' 
                : 'bg-white text-gray-800 border-gray-300 focus:border-blue-500'
              }
              border focus:outline-none focus:ring-1 focus:ring-blue-500
              transition
            `}
            placeholder="Enter package name"
            required
          />
        </div>

        {/* Description Input */}
        <div>
          <label 
            htmlFor="description" 
            className={`block mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
          >
            Description
          </label>
          <textarea
            id="description"
            value={packageData.description}
            onChange={(e) => setPackageData({...packageData, description: e.target.value})}
            className={`
              w-full px-3 py-2 rounded-lg
              ${isDarkMode 
                ? 'bg-gray-700 text-gray-200 border-gray-600 focus:border-blue-500' 
                : 'bg-white text-gray-800 border-gray-300 focus:border-blue-500'
              }
              border focus:outline-none focus:ring-1 focus:ring-blue-500
              transition
            `}
            placeholder="Enter package description"
            rows="3"
          />
        </div>

        {/* Duration Input */}
        <div>
          <label 
            htmlFor="duration" 
            className={`block mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
          >
            Duration (days) *
          </label>
          <input
            type="number"
            id="duration"
            value={packageData.duration}
            onChange={(e) => setPackageData({...packageData, duration: e.target.value})}
            className={`
              w-full px-3 py-2 rounded-lg
              ${isDarkMode 
                ? 'bg-gray-700 text-gray-200 border-gray-600 focus:border-blue-500' 
                : 'bg-white text-gray-800 border-gray-300 focus:border-blue-500'
              }
              border focus:outline-none focus:ring-1 focus:ring-blue-500
              transition
            `}
            placeholder="Enter duration in days"
            min="1"
            required
          />
        </div>

        {/* Price Input */}
        <div>
          <label 
            htmlFor="price" 
            className={`block mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
          >
            Price *
          </label>
          <input
            type="number"
            id="price"
            value={packageData.price}
            onChange={(e) => setPackageData({...packageData, price: e.target.value})}
            className={`
              w-full px-3 py-2 rounded-lg
              ${isDarkMode 
                ? 'bg-gray-700 text-gray-200 border-gray-600 focus:border-blue-500' 
                : 'bg-white text-gray-800 border-gray-300 focus:border-blue-500'
              }
              border focus:outline-none focus:ring-1 focus:ring-blue-500
              transition
            `}
            placeholder="Enter price"
            min="0"
            step="0.01"
            required
          />
        </div>
      </ManagementModal>

      {/* Packages List */}
      <div className={`
        ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
        shadow-lg rounded-xl border 
        transition-colors duration-300
      `}>
        {/* Card Header */}
        <div className={`
          px-6 py-4 
          ${isDarkMode ? 'border-b-gray-700' : 'border-b-gray-200'}
          border-b 
          flex justify-between items-center
        `}>
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
            Subscription Packages
          </h2>
          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Total: {packages.length} packages
          </div>
        </div>

        {/* Packages Table */}
        <div className="overflow-x-auto">
          {packages.length === 0 ? (
            <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No subscription packages found
            </div>
          ) : (
            <table className="w-full text-sm text-left">
              <thead className={`
                ${isDarkMode 
                  ? 'bg-gray-700 text-gray-300' 
                  : 'bg-gray-50 text-gray-600'
                } 
                border-b border-gray-200 dark:border-gray-600
              `}>
                <tr>
                  <th className="px-6 py-3 text-left">Name</th>
                  <th className="px-6 py-3 text-left">Description</th>
                  <th className="px-6 py-3 text-left">Duration</th>
                  <th className="px-6 py-3 text-left">Price</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {packages.map((pkg) => (
                  <tr 
                    key={pkg.id} 
                    className={`
                      border-b 
                      ${isDarkMode 
                        ? 'border-gray-700 hover:bg-gray-700' 
                        : 'border-gray-200 hover:bg-gray-50'
                      }
                      transition duration-150
                    `}
                  >
                    <td className={`px-6 py-4 font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {pkg.name}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {pkg.description || '-'}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {pkg.duration} days
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      ${pkg.price.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end space-x-2">
                        <button 
                          onClick={() => handleEdit(pkg)}
                          className={`
                            p-2 rounded-full 
                            ${isDarkMode 
                              ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-700' 
                              : 'text-blue-500 hover:text-blue-700 hover:bg-blue-100'
                            }
                            transition
                          `}
                          title="Edit"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button 
                          onClick={() => handleDelete(pkg.id)}
                          className={`
                            p-2 rounded-full 
                            ${isDarkMode 
                              ? 'text-red-400 hover:text-red-300 hover:bg-red-700' 
                              : 'text-red-500 hover:text-red-700 hover:bg-red-100'
                            }
                            transition
                          `}
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPackageManagement;